import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Trash2,
  MapPin, ExternalLink, Package, X, Calculator,
} from 'lucide-react';
import {
  FLOOR_PREP_PRODUCTS,
  EPOXY_PRIMERS,
  DESIGN_COAT_PRODUCTS,
  PIGMENTS_PRODUCTS,
  TOP_COAT_PRODUCTS,
} from '@/components/proepoxy/productData';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import PrioritySelector from '@/components/PrioritySelector';

export default function MetallicStep3({ state, priority, setPriority, currentStep, goToStep, navigate, onExit }) {
  const {
    primerAreas, setPrimerAreas, designCoatSelections, pigmentSelections, topCoatSelections,
    steps,
    addPrimerArea, removePrimerArea, updatePrimerArea, updateAreaPackQty,
    updateAreaField, updateAreaFloorPrepQty,
    initAreaTopCoat, updateAreaTopCoatFinish, updateAreaTopCoatPackQty,
    getAreaTopCoatSubtotal, getAreaCoverage, getAreaSubtotal,
    initializeDesignCoatForArea, updateDesignCoatProduct,
    updateDesignCoatPackQtyLegacy, updateCrystalUnitsLegacy,
    getDesignCoatSubtotal, getDesignCoatCoverage,
    selectTopCoatProduct, updateTopCoatFinishLegacy, updateTopCoatPackQtyLegacy,
    getTopCoatSubtotal, getTopCoatCoverage,
    addMetallicPowder, updateMetallicPowderQty,
    addPigmentPaste, removePigmentPaste, updatePigmentPastePackQty,
    addGlitter, removeGlitter, updateGlitterPackQty,
    addSpray, updateSprayQty, getPigmentsSubtotal,
    calculateTotal, getItemCount,
  } = state;
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-[#27292E]">Primer</h2>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calculator className="w-4 h-4" />
                      Auto-calculates packs
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Add each room/area separately. Enter the m² and we&apos;ll calculate the optimal pack combination.
                  </p>

                  {/* Primer Areas List */}
                  {primerAreas.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                      <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 mb-4">No areas added yet</p>
                      <Button
                        onClick={addPrimerArea}
                        className="bg-[#7A0B7A] hover:bg-[#5a085a] text-white"
                        data-testid="add-first-area-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Area
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {primerAreas.map((area, index) => (
                        <div
                          key={area.id}
                          className="border-2 border-gray-200 rounded-xl p-4 hover:border-[#7A0B7A]/30 transition-colors"
                          data-testid={`primer-area-${index}`}
                        >
                          {/* Area Header */}
                          <div className="flex items-center gap-3 mb-4">
                            <Input
                              type="text"
                              value={area.name}
                              onChange={(e) => updatePrimerArea(area.id, 'name', e.target.value)}
                              className="flex-1 font-medium border-2 rounded-lg"
                              placeholder="Area name (e.g., Kitchen)"
                              data-testid={`area-name-${index}`}
                            />
                            <button
                              onClick={() => removePrimerArea(area.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove area"
                              data-testid={`remove-area-${index}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>

                          {/* Primer Type & Color Selection */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                            {/* Primer Type */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Primer Type</label>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => updatePrimerArea(area.id, 'primerType', '1_part')}
                                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                                    area.primerType === '1_part'
                                      ? 'bg-[#7A0B7A] text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                  data-testid={`area-${index}-1part`}
                                >
                                  1-Part
                                </button>
                                <button
                                  onClick={() => updatePrimerArea(area.id, 'primerType', '2_part')}
                                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                                    area.primerType === '2_part'
                                      ? 'bg-[#7A0B7A] text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                  data-testid={`area-${index}-2part`}
                                >
                                  2-Part
                                </button>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {EPOXY_PRIMERS[area.primerType].name}
                              </p>
                            </div>

                            {/* Color Selection */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                              <div className="flex gap-2 flex-wrap">
                                {EPOXY_PRIMERS[area.primerType].colors.map(color => (
                                  <button
                                    key={color}
                                    onClick={() => updatePrimerArea(area.id, 'color', color)}
                                    className={`py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                                      area.color === color
                                        ? 'bg-[#7A0B7A] text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                    data-testid={`area-${index}-color-${color.toLowerCase()}`}
                                  >
                                    {color}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* M² Input */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Total Area Size (m²)
                            </label>
                            <div className="flex items-center gap-3">
                              <Input
                                type="number"
                                min="1"
                                value={area.m2}
                                onChange={(e) => updatePrimerArea(area.id, 'm2', e.target.value)}
                                className="w-32 border-2 rounded-lg text-lg font-medium"
                                placeholder="0"
                                data-testid={`area-m2-${index}`}
                              />
                              <span className="text-gray-500">m²</span>
                              {area.m2 && parseInt(area.m2) > 0 && (
                                <span className="text-sm text-[#7A0B7A] font-medium">
                                  → {getAreaCoverage(area)}m² coverage
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Calculated Packs */}
                          {area.packs.length > 0 && (
                            <div className="bg-gray-50 rounded-xl p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-medium text-[#27292E]">Recommended Packs</h4>
                                <span className="text-xs text-gray-500">Adjust quantities if needed</span>
                              </div>
                              <div className="space-y-2">
                                {EPOXY_PRIMERS[area.primerType].packs.map(packOption => {
                                  const areaPack = area.packs.find(p => p.size === packOption.size);
                                  const qty = areaPack ? areaPack.quantity : 0;
                                  return (
                                    <div
                                      key={packOption.size}
                                      className={`p-4 rounded-lg ${
                                        qty > 0 ? 'bg-white border-2 border-[#7A0B7A]/20' : 'bg-white/50'
                                      }`}
                                    >
                                      {/* Top row: Pack size left, quantity controls right */}
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-[#27292E]">{packOption.size}m² pack</span>
                                        <div className="flex items-center gap-2">
                                          <button
                                            onClick={() => updateAreaPackQty(area.id, packOption.size, -1)}
                                            className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/10 transition-colors"
                                            data-testid={`area-${index}-pack-${packOption.size}-minus`}
                                          >
                                            <Minus className="w-4 h-4" />
                                          </button>
                                          <span className="w-8 text-center font-bold text-lg">{qty}</span>
                                          <button
                                            onClick={() => updateAreaPackQty(area.id, packOption.size, 1)}
                                            className="w-9 h-9 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] bg-[#7A0B7A] text-white hover:bg-[#5a085a] transition-colors"
                                            data-testid={`area-${index}-pack-${packOption.size}-plus`}
                                          >
                                            <Plus className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                      {/* Bottom row: Unit price left, total right */}
                                      <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500">£{packOption.price.toFixed(2)} each</span>
                                        {qty > 0 && (
                                          <span className="font-bold text-[#7A0B7A]">
                                            Total: £{(qty * packOption.price).toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              {/* Area Subtotal */}
                              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between">
                                <span className="text-gray-600">
                                  {area.name} subtotal ({area.color} {area.primerType === '1_part' ? '1-Part' : '2-Part'})
                                </span>
                                <span className="font-bold text-[#7A0B7A]">£{getAreaSubtotal(area).toFixed(2)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Add Another Area Button */}
                      <button
                        onClick={addPrimerArea}
                        className="w-full py-4 border-2 border-dashed border-[#7A0B7A]/30 rounded-xl text-[#7A0B7A] font-medium hover:bg-[#7A0B7A]/5 hover:border-[#7A0B7A] transition-colors flex items-center justify-center gap-2"
                        data-testid="add-another-area-btn"
                      >
                        <Plus className="w-5 h-5" />
                        Add Another Area
                      </button>
                    </div>
                  )}
                </div>

                {/* Running Total */}
                <div className="bg-[#7A0B7A] text-white rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white/80 text-sm">Order Total</p>
                      <p className="text-xs text-white/60">{getItemCount()} item{getItemCount() !== 1 ? 's' : ''} • Excl. VAT</p>
                    </div>
                    <p className="text-3xl font-bold">
                      £{calculateTotal().toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => goToStep(2)}
                    variant="outline"
                    className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5"
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(4)}
                    className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
  );
}
